<script setup lang="ts">
import { tpgIconClose, tpgIconSearch } from '@tpg/grammar-web-icons';
import { centroid } from '@turf/turf';
import { storeToRefs } from 'pinia';
import { onUnmounted, ref } from 'vue';

import { GrammarPrimaryButton } from '@nora-monorepo/grammar-ui';
import type { GeocodingFeature } from '@nora-monorepo/nora-api-client';
import { useWebViewStore } from '@nora-monorepo/ui-shared';

import { GeoSearchNameGenerator } from '#nora-ui/components/geo-search/geo-search.utils';
import GeoSearch from '#nora-ui/components/geo-search/GeoSearch.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface Props {
  mapId: string;
}

const props = defineProps<Props>();

const mapStore = useMapStore();
const webViewStore = useWebViewStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const { isWebView } = storeToRefs(webViewStore);
const showSearch = ref(false);

let markerId: string | null = null;

function clearSearchMarker() {
  if (markerId) {
    map.state.marker.removeMarker(markerId);

    markerId = null;
  }
}

function toggleSearch() {
  showSearch.value = !showSearch.value;

  clearSearchMarker();
}

/**
 * Adds map markers on search results.
 * @param feature
 */
function onLocationSelected(feature: GeocodingFeature) {
  const [longitude, latitude] = centroid(feature).geometry.coordinates;

  if (latitude === undefined || longitude === undefined) {
    return;
  }

  map.setCenter({ latitude, longitude, zoom: 15 });

  clearSearchMarker();

  markerId = map.state.marker.addMarker(
    {
      latitude,
      longitude,
      name: GeoSearchNameGenerator.generateDisplayName(feature),
    },
  );
}

onUnmounted(() => {
  clearSearchMarker();
});
</script>

<template>
  <div
    class="flex flex-row absolute z-30 gap-2 pointer-events-none left-5 right-5 justify-end"
    :class="[
      isWebView && `top-4`,
      !isWebView && `top-16`,
    ]"
  >
    <div
      v-if="showSearch"
      class="pointer-events-auto"
      :class="[isWebView && `w-full`,
               !isWebView && `w-[320px]`,
      ]"
    >
      <GeoSearch
        class="map-search"
        :map-id="mapId"
        placeholder="Search for place or address "
        @feature-selected="onLocationSelected"
      />
    </div>

    <GrammarPrimaryButton
      class="pointer-events-auto"
      :icon-template="showSearch ? tpgIconClose : tpgIconSearch"
      @click="toggleSearch"
    />
  </div>
</template>

<style scoped>
/* Workaround for transparent search field */
.map-search :deep(tpg-text-field) {
  --formsTextFieldColorBackgroundMainActivated: var(--colorFormBackgroundDefault);
  --formsTextFieldColorBackgroundMainEnabled: var(--colorFormBackgroundDefault);
  --formsTextFieldColorBackgroundMainPlaceholder: var(--colorFormBackgroundDefault);
}
</style>
